/**
 * Binance Futures Client
 *
 * Connects to Binance USDⓈ-M Futures via CCXT using public market data streams.
 * Provides real-time mark prices, funding rates, and best bid/ask prices
 * without requiring authentication.
 *
 * Streams used:
 * - watchMarkPrices(): !markPrice@arr - funding rates and mark prices (1s updates)
 * - watchBidsAsks(): !bookTicker - best bid/ask prices (real-time updates)
 *
 * Symbol Discovery:
 * - Fetches symbols from /api/f/symbols endpoint first
 * - Falls back to discovering from mark price stream if API unavailable
 */

import type { Exchange as CcxtExchange } from 'ccxt';
import type { MarkPriceData, FuturesOrderBook } from './types';
import { isFresh } from './types';
import { BinanceFuturesBookTickerClient, type BookTickerData } from './binance-futures-bookticker';

/**
 * API endpoint for futures symbols
 */
const FUTURES_SYMBOLS_API = '/api/f/symbols';

// Dynamic CCXT import (browser-compatible)
type CcxtModule = typeof import('ccxt');
let ccxtModulePromise: Promise<CcxtModule> | null = null;

async function loadCcxtModule(): Promise<CcxtModule> {
  if (!ccxtModulePromise) {
    ccxtModulePromise = import('ccxt').then((raw) => {
      return (raw.default ?? raw) as CcxtModule;
    });
  }
  return ccxtModulePromise;
}

/**
 * Maximum symbols per orderbook subscription (CCXT/Binance limit)
 */
const MAX_SYMBOLS_PER_SUBSCRIPTION = 200;

/**
 * Orderbook depth limit (balance between data detail and bandwidth)
 * Valid Binance Futures depths: 5, 10, 20, 50, 100, 500, 1000
 */
const ORDERBOOK_DEPTH_LIMIT = 20;

/**
 * Exponential backoff configuration
 */
const INITIAL_RETRY_DELAY_MS = 1000; // 1 second
const MAX_RETRY_DELAY_MS = 60000; // 60 seconds
const BACKOFF_MULTIPLIER = 2;

/**
 * Binance Futures Client Class
 */
export class BinanceFuturesClient {
  private exchange: any; // Using any because ccxt.pro.binanceusdm type is complex
  private futuresMarkPrices: Map<string, MarkPriceData>;
  private usdtSymbols: string[];
  private connected: boolean;
  private reconnectAttempts: number;
  private currentRetryDelay: number;
  private ccxtReady: Promise<void>;

  /** Native WebSocket client for book ticker (more reliable than CCXT's watchBidsAsks) */
  private bookTickerClient: BinanceFuturesBookTickerClient;

  constructor() {
    // Initialize storage for mark prices (orderbooks stored in exchange.orderbooks)
    this.futuresMarkPrices = new Map();
    this.usdtSymbols = [];
    this.connected = false;

    // Initialize reconnection state
    this.reconnectAttempts = 0;
    this.currentRetryDelay = INITIAL_RETRY_DELAY_MS;

    // Initialize native book ticker client (more reliable than CCXT for Binance Futures)
    this.bookTickerClient = new BinanceFuturesBookTickerClient();

    // Set up callback to update mark prices when book ticker data arrives
    this.bookTickerClient.setOnBookTickerUpdate((bookTicker: BookTickerData) => {
      this.handleBookTickerUpdate(bookTicker);
    });

    // Log connection state changes
    this.bookTickerClient.setOnConnectionStateChange((state) => {
      console.log(`[Binance Futures] Book ticker connection state: ${state}`);
    });

    // Load CCXT dynamically and initialize exchange
    this.ccxtReady = loadCcxtModule().then((ccxtModule) => {
      // Initialize binanceusdm exchange with WebSocket support (Pro)
      this.exchange = new ccxtModule.pro.binanceusdm({
        enableRateLimit: true, // Respect rate limits for public data
        // No API keys required for public streams
      });
    });
  }

  /**
   * Handle book ticker update from native WebSocket client
   * Updates bestBid/bestAsk in mark price data
   */
  private handleBookTickerUpdate(bookTicker: BookTickerData): void {
    const { symbol, bestBid, bestBidQty, bestAsk, bestAskQty } = bookTicker;

    // Get or create mark price data for this symbol
    let existingData = this.futuresMarkPrices.get(symbol);

    if (!existingData) {
      // Create initial data if it doesn't exist yet
      existingData = {
        symbol,
        markPrice: bestBid, // Use bestBid as fallback for mark price
        lastPrice: bestBid,
        indexPrice: 0,
        bestBid: 0,
        bestBidQty: 0,
        bestAsk: 0,
        bestAskQty: 0,
        fundingRate: 0,
        nextFundingTime: 0,
        timestamp: Date.now(),
      };
      this.futuresMarkPrices.set(symbol, existingData);
    }

    // Update best bid/ask from book ticker
    existingData.bestBid = bestBid;
    existingData.bestBidQty = bestBidQty;
    existingData.bestAsk = bestAsk;
    existingData.bestAskQty = bestAskQty;
    // Also update lastPrice and markPrice from bid for consistency
    existingData.lastPrice = bestBid;
    existingData.markPrice = bestBid;
    existingData.timestamp = Date.now();
  }

  /**
   * Subscribe to mark price stream for ALL symbols
   * Uses Binance's !markPrice@arr stream to get all mark prices efficiently
   * Updates every 1 second with use1sFreq enabled
   *
   * On first data receipt, extracts USDT perpetual symbols and starts orderbook streams
   */
  async watchMarkPrices(): Promise<void> {
    try {
      console.log('[Binance Futures] Starting mark price stream (1s frequency, all symbols)...');
      let orderbooksStarted = false;

      while (this.connected) {
        try {
          // Subscribe to ALL mark prices using undefined (Binance !markPrice@arr stream)
          // use1sFreq: true = 1-second update frequency
          const markPrices = await this.exchange.watchMarkPrices(undefined, { use1sFreq: true });

          // Reset reconnect state on successful data receipt
          this.resetReconnectState();

          // Update storage map with received data
          if (markPrices) {
            // watchMarkPrices returns a dictionary of mark prices
            for (const symbol in markPrices) {
              const markPrice = markPrices[symbol];

              if (markPrice) {
                // CRITICAL: Only process USDT-quoted perpetual futures
                // Format: "BTC/USDT:USDT" for perpetuals
                // Skip USDC, BUSD, and delivery contracts
                if (!markPrice.symbol.includes(':USDT')) {
                  continue;
                }

                // CRITICAL: Normalize CCXT futures symbols by removing ":USDT" suffix
                // CCXT returns "BTC/USDT:USDT", we normalize to "BTC/USDT" for easier lookups
                const normalizedSymbol = markPrice.symbol.replace(':USDT', '');

                // Skip delivery contracts (quarterly futures with dates like BTC/USDT-251226)
                // We only want perpetual swaps
                if (normalizedSymbol.includes('-')) {
                  continue;
                }

                // Binance returns funding rate as 'r' and next funding time as 'T' in info
                // T is in milliseconds (e.g., 1562306400000)
                const fundingRate = parseFloat(markPrice.info?.r || '0');
                const nextFundingTime = parseInt(markPrice.info?.T || '0', 10);

                // Check if we already have data for this symbol
                const existingData = this.futuresMarkPrices.get(normalizedSymbol);

                if (existingData) {
                  // Update ONLY funding rate and funding time
                  // DO NOT update prices here - book ticker stream is the ONLY source for prices
                  existingData.fundingRate = fundingRate;
                  existingData.nextFundingTime = nextFundingTime;
                  existingData.timestamp = Date.now();
                } else {
                  // First time seeing this symbol - create initial data with placeholder values
                  // Book ticker stream will update prices once it receives data
                  const data: MarkPriceData = {
                    symbol: normalizedSymbol,
                    markPrice: 0,
                    lastPrice: 0,
                    indexPrice: 0,
                    bestBid: 0,   // Will be set by book ticker stream ONLY
                    bestBidQty: 0,
                    bestAsk: 0,   // Will be set by book ticker stream ONLY
                    bestAskQty: 0,
                    fundingRate,
                    nextFundingTime,
                    timestamp: Date.now(),
                  };
                  this.futuresMarkPrices.set(normalizedSymbol, data);
                }
              }
            }

            // CRITICAL: Only extract symbols and start orderbooks ONCE on first data receipt
            // Do NOT rebuild symbol list on every update - that would subscribe repeatedly!
            if (!orderbooksStarted && this.futuresMarkPrices.size > 0) {
              // Extract USDT perpetual symbols from mark prices (ONCE!)
              // Filter out delivery contracts (they have dates like BTC/USDT-251226)
              // These are already filtered for USDT and normalized
              this.usdtSymbols = Array.from(this.futuresMarkPrices.keys())
                .filter(normalizedSymbol => !normalizedSymbol.includes('-')) // Exclude delivery contracts
                .map(normalizedSymbol => `${normalizedSymbol}:USDT`); // Add back :USDT suffix for CCXT

              console.log(`[Binance Futures] Discovered ${this.usdtSymbols.length} USDT perpetual swaps from mark prices`);
              console.log(`[Binance Futures] NOT subscribing to orderbooks (per Epic 10 - mark prices only)`);

              // DISABLED: Epic 10 specifies NOT to subscribe to orderbooks for futures
              // Orderbooks should only be fetched on-demand when user clicks action button (Story 10.4)
              // We only need mark prices and funding rates for the table view
              // this.watchOrderBooks().catch(err => {
              //   console.error('[Binance Futures] Orderbook stream failed:', err);
              // });

              // Mark as started to prevent re-subscription on every mark price update
              orderbooksStarted = true;
            }
          }
        } catch (error) {
          console.error('[Binance Futures] Mark price stream error:', error);
          await this.handleReconnection('Mark price stream');
        }
      }
    } catch (error) {
      console.error('[Binance Futures] Failed to start mark price stream:', error);
      throw error;
    }
  }

  /**
   * Subscribe to orderbook streams for all USDT symbols
   * Automatically creates multiple subscriptions if >200 symbols
   */
  async watchOrderBooks(): Promise<void> {
    try {
      if (this.usdtSymbols.length === 0) {
        throw new Error('No USDT symbols loaded. Call fetchUSDTMarkets() first.');
      }

      console.log(`[Binance Futures] Starting orderbook streams for ${this.usdtSymbols.length} symbols...`);

      // Split symbols into batches of 200 (CCXT limit)
      const batches = this.chunkArray(this.usdtSymbols, MAX_SYMBOLS_PER_SUBSCRIPTION);
      console.log(`[Binance Futures] Created ${batches.length} orderbook subscriptions`);

      // Create concurrent subscriptions for each batch
      const subscriptionPromises = batches.map((batch, index) =>
        this.watchOrderBookBatch(batch, index)
      );

      // Wait for all subscriptions to start
      await Promise.all(subscriptionPromises);
    } catch (error) {
      console.error('[Binance Futures] Failed to start orderbook streams:', error);
      throw error;
    }
  }

  /**
   * Watch orderbook for a batch of symbols
   * @param symbols - Array of symbols (max 200)
   * @param batchIndex - Index for logging purposes
   */
  private async watchOrderBookBatch(symbols: string[], batchIndex: number): Promise<void> {
    console.log(`[Binance Futures] Batch ${batchIndex + 1}: Watching ${symbols.length} symbols`);

    while (this.connected) {
      try {
        // Subscribe to all symbols in batch at once
        // CCXT maintains ALL orderbooks in exchange.orderbooks cache
        // We just need to keep the subscription alive
        await this.exchange.watchOrderBookForSymbols(
          symbols,
          ORDERBOOK_DEPTH_LIMIT
        );

        // Reset reconnect state on successful data receipt
        this.resetReconnectState();

        // No need to copy - we read directly from exchange.orderbooks
      } catch (error) {
        console.error(`[Binance Futures] Batch ${batchIndex + 1} orderbook error:`, error);
        await this.handleReconnection(`Orderbook batch ${batchIndex + 1}`);
      }
    }
  }

  /**
   * Get mark price data for a symbol
   * @param symbol - Trading pair symbol
   * @returns Mark price data or undefined if not available or stale
   */
  getMarkPrice(symbol: string): MarkPriceData | undefined {
    const data = this.futuresMarkPrices.get(symbol);

    if (!data) {
      return undefined;
    }

    // Reject stale data (>30 seconds old)
    if (!isFresh(data)) {
      console.warn(`[Binance Futures] Stale mark price data for ${symbol}`);
      return undefined;
    }

    return data;
  }

  /**
   * Get orderbook data for a symbol
   * @param symbol - Trading pair symbol
   * @returns Orderbook data or undefined if not available or stale
   */
  getOrderBook(symbol: string): FuturesOrderBook | undefined {
    // CCXT stores orderbooks with ":USDT" suffix, so try both formats
    const ccxtSymbol = symbol.includes(':') ? symbol : `${symbol}:USDT`;

    // Read directly from CCXT's internal cache
    if (!this.exchange.orderbooks || !this.exchange.orderbooks[ccxtSymbol]) {
      return undefined;
    }

    const orderbook = this.exchange.orderbooks[ccxtSymbol];

    // Convert CCXT orderbook to our format with timestamp
    // Normalize symbol by removing ":USDT" suffix
    const data: FuturesOrderBook = {
      symbol: orderbook.symbol.replace(':USDT', ''),
      bids: orderbook.bids,
      asks: orderbook.asks,
      timestamp: orderbook.timestamp || Date.now(),
    };

    // Reject stale data (>30 seconds old)
    if (!isFresh(data)) {
      console.warn(`[Binance Futures] Stale orderbook data for ${symbol}`);
      return undefined;
    }

    return data;
  }

  /**
   * Get all mark prices (filtered for freshness)
   * @returns Array of fresh mark price data
   */
  getAllMarkPrices(): MarkPriceData[] {
    return Array.from(this.futuresMarkPrices.values()).filter(isFresh);
  }

  /**
   * Get all orderbooks (filtered for freshness)
   * @returns Array of fresh orderbook data
   */
  getAllOrderBooks(): FuturesOrderBook[] {
    if (!this.exchange.orderbooks) {
      return [];
    }

    // Convert CCXT orderbooks to our format and filter for freshness
    const orderbooks: FuturesOrderBook[] = [];

    for (const symbol in this.exchange.orderbooks) {
      const orderbook = this.exchange.orderbooks[symbol];
      if (orderbook && orderbook.symbol) {
        // Normalize symbol by removing ":USDT" suffix
        const data: FuturesOrderBook = {
          symbol: orderbook.symbol.replace(':USDT', ''),
          bids: orderbook.bids,
          asks: orderbook.asks,
          timestamp: orderbook.timestamp || Date.now(),
        };

        if (isFresh(data)) {
          orderbooks.push(data);
        }
      }
    }

    return orderbooks;
  }

  /**
   * Subscribe to book ticker stream for best bid/ask prices
   * Uses CCXT's watchBidsAsks which requires specific symbols
   * Updates in real-time with best bid/ask prices (more accurate than lastPrice for trading)
   *
   * Note: CCXT's watchBidsAsks requires symbols array - it doesn't support undefined
   * for all-symbols subscription. We fetch symbols from API first.
   */
  async watchBidsAsks(): Promise<void> {
    try {
      console.log('[Binance Futures] Fetching symbols for book ticker stream...');

      // Wait for exchange markets to load (required for CCXT symbol validation)
      if (!this.exchange.markets) {
        console.log('[Binance Futures] Loading markets...');
        await this.exchange.loadMarkets();
      }

      // Get ALL symbols from API (these are verified to exist on the exchange)
      let symbols = await this.fetchSymbolsFromApi();

      if (symbols.length === 0) {
        // Fallback: use symbols discovered from mark price stream
        console.log('[Binance Futures] API unavailable, waiting for symbols from mark price stream...');
        while (this.connected && this.usdtSymbols.length === 0) {
          await this.sleep(100);
        }
        symbols = this.usdtSymbols;
      } else {
        this.usdtSymbols = symbols;
      }

      if (!this.connected) return;

      console.log(`[Binance Futures] Starting book ticker stream for ${symbols.length} symbols...`);

      // Split symbols into batches (same as orderbook subscription pattern)
      const batches = this.chunkArray(symbols, MAX_SYMBOLS_PER_SUBSCRIPTION);
      console.log(`[Binance Futures] Created ${batches.length} book ticker subscriptions`);

      // Create concurrent subscriptions for each batch
      const subscriptionPromises = batches.map((batch, index) =>
        this.watchBidsAsksBatch(batch, index)
      );

      // Wait for all subscriptions (they run forever until disconnect)
      await Promise.all(subscriptionPromises);
    } catch (error) {
      console.error('[Binance Futures] Failed to start book ticker stream:', error);
      throw error;
    }
  }

  /**
   * Watch book tickers for a batch of symbols
   * @param symbols - Array of symbols (max 200)
   * @param batchIndex - Index for logging purposes
   */
  private async watchBidsAsksBatch(symbols: string[], batchIndex: number): Promise<void> {
    console.log(`[Binance Futures] Book ticker batch ${batchIndex + 1}: Watching ${symbols.length} symbols`);

    while (this.connected) {
      try {
        // Subscribe to book tickers with specific symbols
        // CCXT requires symbols in format: ['BTC/USDT:USDT', 'ETH/USDT:USDT', ...]
        const tickers = await this.exchange.watchBidsAsks(symbols);

        // Reset reconnect state on successful data receipt
        this.resetReconnectState();

        // Update bestBid/bestAsk in existing mark price data
        if (tickers) {
          for (const symbol in tickers) {
            const ticker = tickers[symbol];

            if (ticker && ticker.symbol.includes(':USDT')) {
              // Normalize symbol by removing :USDT suffix
              const normalizedSymbol = ticker.symbol.replace(':USDT', '');

              // Skip delivery contracts
              if (normalizedSymbol.includes('-')) {
                continue;
              }

              // Get or create mark price data for this symbol
              let existingData = this.futuresMarkPrices.get(normalizedSymbol);

              if (!existingData) {
                // Create initial data if it doesn't exist yet
                existingData = {
                  symbol: normalizedSymbol,
                  markPrice: 0,
                  lastPrice: 0,
                  indexPrice: 0,
                  bestBid: 0,
                  bestBidQty: 0,
                  bestAsk: 0,
                  bestAskQty: 0,
                  fundingRate: 0,
                  nextFundingTime: 0,
                  timestamp: Date.now(),
                };
                this.futuresMarkPrices.set(normalizedSymbol, existingData);
              }

              // Update best bid/ask from book ticker
              // Use truthy check to handle both null and undefined
              if (ticker.bid) {
                existingData.bestBid = ticker.bid;
                // Also update lastPrice and markPrice from bid for backwards compatibility
                // Best bid is what we'd get when opening a short position
                existingData.lastPrice = ticker.bid;
                existingData.markPrice = ticker.bid;
              }
              if (ticker.bidVolume) {
                existingData.bestBidQty = ticker.bidVolume;
              }
              if (ticker.ask) {
                existingData.bestAsk = ticker.ask;
              }
              if (ticker.askVolume) {
                existingData.bestAskQty = ticker.askVolume;
              }
              existingData.timestamp = Date.now();
            }
          }
        }
      } catch (error) {
        console.error(`[Binance Futures] Book ticker batch ${batchIndex + 1} error:`, error);
        await this.handleReconnection(`Book ticker batch ${batchIndex + 1}`);
      }
    }
  }

  /**
   * Connect and start all data streams
   *
   * Starts mark price stream first, which automatically discovers USDT perpetual
   * symbols and starts ticker stream for last prices once data is received.
   */
  async connect(): Promise<void> {
    try {
      // Wait for CCXT module to load
      await this.ccxtReady;

      console.log('[Binance Futures] Connecting...');
      this.connected = true;

      // Start mark price stream (runs in background)
      // This will automatically:
      // 1. Receive all mark prices for USDT perpetuals
      // 2. Extract and store USDT perpetual symbols
      // 3. Provide funding rates ONLY (not prices!)
      this.watchMarkPrices().catch(err => {
        console.error('[Binance Futures] Mark price stream failed:', err);
      });

      // Start native book ticker stream to get best bid/ask prices
      // Uses wss://fstream.binance.com/ws/!bookTicker (all symbols in one stream)
      // This is more reliable than CCXT's watchBidsAsks which doesn't work well for Binance Futures
      console.log('[Binance Futures] Starting native book ticker stream...');
      this.bookTickerClient.connect();

      // Connection is complete - both streams are running
      console.log('[Binance Futures] Connected successfully (mark price + native book ticker streams started)');
    } catch (error) {
      console.error('[Binance Futures] Connection failed:', error);
      this.connected = false;
      throw error;
    }
  }

  /**
   * Disconnect and cleanup
   */
  async disconnect(): Promise<void> {
    console.log('[Binance Futures] Disconnecting...');
    this.connected = false;

    // Disconnect native book ticker client
    this.bookTickerClient.disconnect();

    try {
      await this.exchange.close();
      console.log('[Binance Futures] Disconnected successfully');
    } catch (error) {
      console.error('[Binance Futures] Disconnect error:', error);
    }

    // Clear all stale data to prevent UI displaying outdated information
    this.clearAllData();
  }

  /**
   * Clear all stored data (mark prices and orderbooks)
   * Called on disconnect to prevent stale data display
   */
  private clearAllData(): void {
    console.log('[Binance Futures] Clearing all stored data...');
    this.futuresMarkPrices.clear();
    // Note: CCXT's exchange.orderbooks is cleared automatically by exchange.close()
  }

  /**
   * Check if client is connected
   */
  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Get connection status
   */
  getStatus(): {
    connected: boolean;
    usdtSymbols: number;
    markPrices: number;
    orderbooks: number;
    bookTickerConnected: boolean;
  } {
    // Count orderbooks from CCXT's internal cache
    const orderbookCount = this.exchange.orderbooks
      ? Object.keys(this.exchange.orderbooks).length
      : 0;

    return {
      connected: this.connected,
      usdtSymbols: this.usdtSymbols.length,
      markPrices: this.futuresMarkPrices.size,
      orderbooks: orderbookCount,
      bookTickerConnected: this.bookTickerClient.isConnected(),
    };
  }

  // Helper methods

  /**
   * Handle reconnection with exponential backoff
   * @param streamName - Name of the stream for logging
   */
  private async handleReconnection(streamName: string): Promise<void> {
    this.reconnectAttempts++;

    console.warn(
      `[Binance Futures] ${streamName} reconnecting... ` +
      `Attempt ${this.reconnectAttempts}, delay: ${this.currentRetryDelay}ms`
    );

    await this.sleep(this.currentRetryDelay);

    // Calculate next retry delay with exponential backoff
    this.currentRetryDelay = Math.min(
      this.currentRetryDelay * BACKOFF_MULTIPLIER,
      MAX_RETRY_DELAY_MS
    );
  }

  /**
   * Reset reconnection state after successful connection
   */
  private resetReconnectState(): void {
    if (this.reconnectAttempts > 0) {
      console.log('[Binance Futures] Connection restored');
      this.reconnectAttempts = 0;
      this.currentRetryDelay = INITIAL_RETRY_DELAY_MS;
    }
  }

  /**
   * Split array into chunks
   */
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  /**
   * Sleep helper for retry logic
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Fetch futures symbols from our API endpoint
   * Converts symbols from API format (BTCUSDT) to CCXT format (BTC/USDT:USDT)
   * @returns Array of CCXT-formatted symbols or empty array if failed
   */
  private async fetchSymbolsFromApi(): Promise<string[]> {
    try {
      console.log('[Binance Futures] Fetching symbols from API...');

      const response = await fetch(FUTURES_SYMBOLS_API);
      if (!response.ok) {
        console.warn(`[Binance Futures] API returned ${response.status}, will discover from mark prices`);
        return [];
      }

      const data = await response.json();
      const symbols: string[] = data.symbols || [];

      if (symbols.length === 0) {
        console.warn('[Binance Futures] API returned empty symbols list');
        return [];
      }

      // Convert from API format (BTCUSDT) to CCXT format (BTC/USDT:USDT)
      const ccxtSymbols = symbols
        .filter((s: string) => s.endsWith('USDT') && !s.includes('_')) // Only USDT perpetuals
        .map((s: string) => {
          const base = s.slice(0, -4); // Remove "USDT"
          return `${base}/USDT:USDT`;
        });

      console.log(`[Binance Futures] Got ${ccxtSymbols.length} symbols from API`);
      return ccxtSymbols;
    } catch (error) {
      console.warn('[Binance Futures] Failed to fetch symbols from API:', error);
      return [];
    }
  }
}
